import { Stack, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { ChannelItemDirection } from '../../../../sic-lib/enums/ChannelItemDirection';
import { ChannelItemStatus } from '../../../../sic-lib/enums/ChannelItemStatus';
import { ChannelType } from '../../../../sic-lib/enums/ChannelType';
import { DateTimeFormat } from '../../../../sic-lib/enums/DateTimeFormat';
import { ChannelItemUnion } from '../../../../sic-lib/types/ChannelItem';
import { Contact } from '../../../../sic-lib/types/Contact';
import { UserInfo } from '../../../../sic-lib/types/UserConfig';
import { UserRoles } from '../../../../sic-lib/types/UserRoles';
import { getFriendlyStatus } from '../../../../sic-lib/utils/channelItemHelpers';
import { formatDateTimeFull } from '../../../../sic-lib/utils/dateHelpers';
import { channelTypeToLabel } from '../../../../sic-lib/utils/timelineHelpers';
import UserChip from '../../../Settings/Users/UserChip';
import DisplayDateTime from '../../../Shared/DisplayDateTime/DisplayDateTime';
import ChannelItemIcon from '../ChannelItemIcon';
import ConversationListItemActionPlan from '../ConversationListItemActionPlan/ConversationListItemActionPlan';
import ConversationListItemAudio from '../ConversationListItemAudio/ConversationListItemAudio';
import ConversationListItemEPlan from '../ConversationListItemEPlan/ConversationListItemEPlan';
import ConversationListItemListing from '../ConversationListItemListing/ConversationListItemListing';
import ConversationListItemMessage from '../ConversationListItemMessage/ConversationListItemMessage';
import ConversationListItemWebEvent from '../ConversationListItemWebEvent/ConversationListItemWebEvent';
import ConversationListItemWebPush from '../ConversationListItemWebPush/ConversationListItemWebPush';
import EmailItem from '../EmailItem/EmailItem';

interface Props {
    contact: Contact;
    channelItem: ChannelItemUnion;
    clientId: number;
    roles: UserRoles[];
    users: UserInfo[];
}

function getTooltip(channelItem: ChannelItemUnion): React.ReactNode {
    const sentDirection = channelItem.direction === ChannelItemDirection.incoming ? 'to' : 'by';

    switch (channelItem.type) {
        case ChannelType.system:
        case ChannelType.webEvents:
        case ChannelType.formRequests:
        case ChannelType.ePlans:
        case ChannelType.listingEvents:
            return (
                <Stack>
                    <Typography variant="caption">
                        Channel: {channelTypeToLabel(channelItem.type)}
                    </Typography>
                </Stack>
            );
        case ChannelType.callEvents:
        case ChannelType.gmailEvents:
        case ChannelType.messageEvents:
        case ChannelType.externalRequests:
        case ChannelType.actionPlans:
        case ChannelType.webPushEvents:
        default:
            return (
                <Stack>
                    <Typography variant="caption">
                        Channel: {channelTypeToLabel(channelItem.type)}
                    </Typography>
                    <Typography variant="caption">
                        Sent {sentDirection}: {channelItem.userName}
                    </Typography>
                    <Typography variant="caption">
                        Status: {getFriendlyStatus(channelItem.status)}
                    </Typography>
                    {!!channelItem.statusMessage && (
                        <Typography variant="caption">
                            Status Message: {channelItem.statusMessage}
                        </Typography>
                    )}
                    {channelItem.type === ChannelType.callEvents && (
                        <Typography variant="caption">Duration: {channelItem.duration}s</Typography>
                    )}
                </Stack>
            );
    }
}

export default React.memo(function ConversationListItem({
    channelItem,
    contact,
    roles,
    users,
}: Props) {
    const { classes, cx } = useStyles();

    const ItemComponent = React.useMemo(() => {
        switch (channelItem.type) {
            case ChannelType.callEvents:
                return <ConversationListItemAudio config={channelItem} />;
            case ChannelType.gmailEvents:
                return <EmailItem channelItem={channelItem} />;
            case ChannelType.messageEvents:
                return <ConversationListItemMessage channelItem={channelItem} roles={roles} />;
            case ChannelType.system:
            case ChannelType.webEvents:
            case ChannelType.formRequests:
            case ChannelType.externalRequests:
                return <ConversationListItemWebEvent channelItem={channelItem} />;
            case ChannelType.actionPlans:
                return <ConversationListItemActionPlan channelItem={channelItem} />;
            case ChannelType.ePlans:
                return <ConversationListItemEPlan channelItem={channelItem} />;
            case ChannelType.listingEvents:
                return <ConversationListItemListing channelItem={channelItem} />;
            case ChannelType.webPushEvents:
                return <ConversationListItemWebPush channelItem={channelItem} />;
            default:
                console.error('Unknown channel item type', channelItem);
                return (
                    <Typography>
                        We were unable to load this event. Trace: {channelItem.type}-
                        {channelItem.id}
                    </Typography>
                );
        }
    }, [channelItem, roles]);

    return (
        <Stack
            flexDirection="column"
            spacing={1}
            p={2}
            alignItems={
                channelItem.direction === ChannelItemDirection.incoming ? 'flex-start' : 'flex-end'
            }
        >
            <Stack
                spacing={1}
                alignItems="center"
                direction={
                    channelItem.direction === ChannelItemDirection.incoming ? 'row' : 'row-reverse'
                }
            >
                <Tooltip title={getTooltip(channelItem)}>
                    <Avatar variant="rounded">
                        <ChannelItemIcon channelItemType={channelItem.subType} />
                    </Avatar>
                </Tooltip>
                <UserChip userName={channelItem.userName || 'System'} users={users} />
                <Tooltip title={formatDateTimeFull(new Date(channelItem.date))}>
                    <span>
                        <DisplayDateTime
                            dateText={channelItem.date}
                            variant="body2"
                            formatPreset={DateTimeFormat.timeSimple}
                        />
                    </span>
                </Tooltip>
                {channelItem.status !== ChannelItemStatus.Completed && (
                    <Tooltip title={channelItem.statusMessage || null}>
                        <Typography
                            variant="caption"
                            color={
                                channelItem.status === ChannelItemStatus.Failed
                                    ? 'error'
                                    : 'textSecondary'
                            }
                        >
                            {getFriendlyStatus(channelItem.status)}
                        </Typography>
                    </Tooltip>
                )}
            </Stack>
            <Paper
                className={cx(
                    classes.paper,
                    channelItem.direction === ChannelItemDirection.incoming
                        ? classes.paperIncoming
                        : classes.paperOutgoing,
                    channelItem.status === ChannelItemStatus.Failed ? classes.failedPaper : '',
                )}
            >
                {ItemComponent}
            </Paper>
            {/* <DebugObject object={channelItem} /> */}
        </Stack>
    );
});

const useStyles = makeStyles()((theme: Theme) => ({
    wrapper: {
        display: 'flex',
        width: '100%',
    },
    wrapperAligned: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        '& > .MuiListItem-root': {
            width: 'auto',
        },
    },
    paper: {
        maxWidth: '800px',
        overflow: 'hidden',
        [theme.breakpoints.up(0)]: {
            minWidth: '240px',
        },
        [theme.breakpoints.up('sm')]: {
            minWidth: '320px',
        },
    },
    paperOutgoing: {
        backgroundColor: theme.palette.conversationTimelinePaper.light,
        color: theme.palette.primary.contrastText,
    },
    paperIncoming: {
        backgroundColor: theme.palette.conversationTimelinePaper.main,
        color: theme.palette.primary.contrastText,
    },
    failedPaper: {
        // border: '2px solid red',
        opacity: 0.75,
    },
}));
